/** Ensure VITE_API_URL works when Cloudflare env omits https:// (e.g. finvox-api.*.workers.dev). */
export function normalizeApiBaseUrl(raw: string | undefined): string {
  const trimmed = raw?.trim().replace(/\/$/, '') ?? '';
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const base = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);

export function getApiBaseUrl(): string {
  return base;
}

/** Local Wrangler dev server (dashboard banner and dev hints only apply here). */
export function isLocalDevApi(): boolean {
  if (!import.meta.env.DEV || !base) return false;
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(base);
}

export type WorkerHealth = {
  ok: boolean;
  firebaseConfigured: boolean;
  apiVersion: number;
  /** Host responded but is not the FinVox API (e.g. default "Hello World" worker). */
  wrongService?: boolean;
};

export function parseWorkerHealthResponse(status: number, text: string): WorkerHealth {
  if (status < 200 || status >= 300) {
    return { ok: false, firebaseConfigured: false, apiVersion: 0 };
  }

  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) {
    return { ok: false, firebaseConfigured: false, apiVersion: 0, wrongService: true };
  }

  try {
    const data = JSON.parse(trimmed) as {
      service?: string;
      firebaseConfigured?: boolean;
      apiVersion?: number;
    };
    if (data.service !== 'finvox-api') {
      return {
        ok: false,
        firebaseConfigured: false,
        apiVersion: data.apiVersion ?? 0,
        wrongService: true
      };
    }
    return {
      ok: true,
      firebaseConfigured: Boolean(data.firebaseConfigured),
      apiVersion: data.apiVersion ?? 0
    };
  } catch {
    return { ok: false, firebaseConfigured: false, apiVersion: 0, wrongService: true };
  }
}

export async function fetchWorkerHealth(): Promise<WorkerHealth> {
  if (!base) return { ok: false, firebaseConfigured: false, apiVersion: 0 };
  try {
    const res = await fetch(`${base}/health`, { cache: 'no-store' });
    const text = await res.text();
    return parseWorkerHealthResponse(res.status, text);
  } catch {
    return { ok: false, firebaseConfigured: false, apiVersion: 0 };
  }
}

/** True when the Worker has Firebase Admin configured and current API routes. */
export async function isWorkerFirebaseReady(): Promise<boolean> {
  const h = await fetchWorkerHealth();
  return h.ok && h.firebaseConfigured && h.apiVersion >= REQUIRED_API_VERSION;
}

const RETRY_DELAY_MS = 400;

/** Must match workers/api/src/index.ts API_VERSION. */
export const REQUIRED_API_VERSION = 13;

export async function isWorkerFirebaseReadyWithRetry(
  attempts = 5
): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    if (await isWorkerFirebaseReady()) return true;
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
    }
  }
  return false;
}

/** False when local dev API is running but missing routes added after the process started. */
export async function isWorkerRoutesCurrent(): Promise<boolean> {
  if (!base || !isLocalDevApi()) return true;
  try {
    const res = await fetch(`${base}/health`, { cache: 'no-store' });
    const health = parseWorkerHealthResponse(res.status, await res.text());
    return health.ok && health.apiVersion >= REQUIRED_API_VERSION;
  } catch {
    return false;
  }
}
